/* <slide-show>: cross-fading slideshow with dots / numbers / arrows, swipe, keyboard, autoplay and
   theme editor support (selecting a slide block shows that slide). */
class SlideShow extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.slideshow__slide')];
    this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-selected')));
    this.seconds = Number(this.dataset.autoplay) || 0;
    this.pausedBy = new Set();
    this.inEditor = Boolean(window.Shopify && window.Shopify.designMode);

    this.onClick = (event) => {
      const to = event.target.closest('[data-slide-to]');
      if (to) { this.goTo(Number(to.dataset.slideTo)); this.restart(); return; }
      if (event.target.closest('[data-slide-prev]')) { this.go(-1); this.restart(); return; }
      if (event.target.closest('[data-slide-next]')) { this.go(1); this.restart(); }
    };
    this.addEventListener('click', this.onClick);

    // Keyboard: arrows move between slides while focus is inside
    this.onKey = (event) => {
      if (this.slides.length < 2) return;
      if (event.key === 'ArrowLeft') { this.go(document.dir === 'rtl' ? 1 : -1); this.restart(); }
      if (event.key === 'ArrowRight') { this.go(document.dir === 'rtl' ? -1 : 1); this.restart(); }
    };
    this.addEventListener('keydown', this.onKey);

    // Swipe
    this.onTouchStart = (event) => {
      this.touchX = event.touches[0].clientX;
      this.touchY = event.touches[0].clientY;
      this.pause('touch');
    };
    this.onTouchEnd = (event) => {
      const t = event.changedTouches[0];
      const dx = t.clientX - this.touchX;
      const dy = t.clientY - this.touchY;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        const rtl = document.dir === 'rtl' ? -1 : 1;
        this.go(dx < 0 ? rtl : -rtl);
      }
      this.resume('touch');
      this.restart();
    };
    this.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.addEventListener('touchend', this.onTouchEnd, { passive: true });

    // Autoplay pauses on hover / focus, and while off screen or in the background tab
    this.onEnter = () => this.pause('hover');
    this.onLeave = () => this.resume('hover');
    this.onFocusIn = () => this.pause('focus');
    this.onFocusOut = () => this.resume('focus');
    this.onVisibility = () => (document.hidden ? this.pause('tab') : this.resume('tab'));
    this.addEventListener('pointerenter', this.onEnter);
    this.addEventListener('pointerleave', this.onLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('visibilitychange', this.onVisibility);
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => (entry.isIntersecting ? this.resume('view') : this.pause('view')));
      this.observer.observe(this);
    }

    // Theme editor
    this.onBlockSelect = (event) => {
      const i = this.slides.indexOf(event.target);
      if (i < 0) return;
      this.pause('editor');
      this.goTo(i, true);
    };
    this.onBlockDeselect = (event) => { if (this.slides.includes(event.target)) this.resume('editor'); };
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    this.sync();
    this.restart();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKey);
    this.removeEventListener('touchstart', this.onTouchStart);
    this.removeEventListener('touchend', this.onTouchEnd);
    this.removeEventListener('pointerenter', this.onEnter);
    this.removeEventListener('pointerleave', this.onLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.observer?.disconnect();
  }

  go(step) {
    const count = this.slides.length;
    if (count < 2) return;
    this.goTo((this.index + step + count) % count);
  }

  goTo(i) {
    if (i < 0 || i >= this.slides.length || i === this.index) return;
    this.index = i;
    this.sync();
  }

  // Applies the selected state to slides and controls and passes the slide's colours to the controls.
  sync() {
    this.slides.forEach((slide, n) => {
      const selected = n === this.index;
      slide.classList.toggle('is-selected', selected);
      if (selected) slide.removeAttribute('inert'); else slide.setAttribute('inert', '');
    });
    this.querySelectorAll('[data-slide-to]').forEach((control) => {
      control.setAttribute('aria-current', String(Number(control.dataset.slideTo) === this.index));
    });
    const slide = this.slides[this.index];
    if (slide) {
      const { textColor, buttonBg, buttonText } = slide.dataset;
      if (textColor && textColor.trim()) this.style.setProperty('--controls-color', textColor);
      if (buttonBg && buttonBg.trim()) this.style.setProperty('--controls-bg', buttonBg);
      if (buttonText && buttonText.trim()) this.style.setProperty('--controls-fg', buttonText);
    }
  }

  get canAutoplay() {
    return this.seconds > 0 && this.slides.length > 1 && !this.inEditor && !matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  pause(reason) {
    this.pausedBy.add(reason);
    this.classList.add('is-paused');
    clearTimeout(this.timer);
  }

  resume(reason) {
    this.pausedBy.delete(reason);
    if (!this.pausedBy.size) { this.classList.remove('is-paused'); this.restart(); }
  }

  // (Re)starts the countdown to the next slide
  restart() {
    clearTimeout(this.timer);
    if (!this.canAutoplay || this.pausedBy.size) return;
    // restart the progress ring of the number controls
    const current = this.querySelector('.slideshow__number[aria-current="true"] .slideshow__number-progress');
    if (current) { current.style.animation = 'none'; void current.getBoundingClientRect(); current.style.animation = ''; }
    this.timer = setTimeout(() => { this.go(1); this.restart(); }, this.seconds * 1000);
  }
}
if (!customElements.get('slide-show')) customElements.define('slide-show', SlideShow);
